import clsx from 'clsx';
import { MouseEvent } from 'react';

import { CardstackContent } from 'src/components/assignments/cardstack-content';
import { assignmentStudentHomeSections } from 'src/constants/xApi.constants';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import {
  AssignmentsViewOptions,
  CardstackAssignment,
} from 'src/interfaces/assignment.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import eventService from 'src/services/event.service';
import { useCurrentGradeSelector } from 'src/store/features';
import { getAssignmentContent } from 'src/utils/assignments.utils';
import { handleSpaceClick } from 'src/utils/keyboard.utils';
import { curriculumURLs } from 'src/utils/url.utils';
import { generateAssignmentDataForEventPayload } from 'src/utils/xapi.utils';

import './cardstack.scss';

export interface CardstackProps {
  assignment: CardstackAssignment;
  k5Section?: AssignmentsViewOptions.PAST_WORK | AssignmentsViewOptions.TODO;
}

export const Cardstack = ({ assignment, k5Section }: CardstackProps) => {
  const { content, isHandedInmCLASSAssessment } = assignment;

  const commonXapiContextPayload = useCommonXapiContextPayloadHook();
  const userCurrentGrade = useCurrentGradeSelector();
  const isToDoSectionActive = k5Section === AssignmentsViewOptions.TODO;
  const isMiddleSchoolView = useIsMiddleSchoolView();

  const { unitKey, lessonKey, chapterKey } = content[0];

  const curriculumUrl = curriculumURLs.getLessonBriefURL(
    unitKey,
    lessonKey,
    chapterKey,
  );

  const onAssignmentClick = (e: MouseEvent) => {
    if (isMiddleSchoolView) {
      return;
    }

    const assignmentDataForEventPayload = generateAssignmentDataForEventPayload(
      {
        assignmentBusinessKey: assignment.assignmentBk,
        assignmentClassBk: assignment.assignmentClassBks,
        classGrade: userCurrentGrade,
        content: getAssignmentContent(assignment),
        assignmentStatus: isToDoSectionActive
          ? assignmentStudentHomeSections[AssignmentsViewOptions.TODO]
          : assignmentStudentHomeSections[AssignmentsViewOptions.PAST_WORK],
      },
    );

    return eventService.send(
      XApiEventKeys.openedAssignment,
      {
        result: {
          destinationUrl: curriculumUrl,
        },
      },
      {
        result: {
          isRightClick: e.type === 'contextmenu',
          ...commonXapiContextPayload,
          ...assignmentDataForEventPayload,
        },
      },
    );
  };

  return (
    <div
      className={clsx('cardstack-assignment', {
        'cardstack-assignment--middle-school': isMiddleSchoolView,
        'cardstack-assignment--elementary-school': !isMiddleSchoolView,
        'cardstack-assignment--elementary-school-to-do':
          !isMiddleSchoolView && isToDoSectionActive,
        'cardstack-assignment--disabled': isHandedInmCLASSAssessment,
      })}
      data-testid="cardstack-assignment"
      onClick={onAssignmentClick}
      onContextMenu={onAssignmentClick}
    >
      <div className="cardstack-assignment__container">
        {!isMiddleSchoolView && (
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a
            className="cardstack-assignment__brief-link"
            href={curriculumUrl}
            rel="noreferrer"
            onKeyDown={handleSpaceClick}
            aria-label="Go to lesson brief"
          />
        )}
        {/* Hidden element for screen readers */}
        <span className="u-hidden" aria-label="Cardstack-Assignment" />
        <div
          className={clsx('cardstack-assignment__details', {
            'cardstack-assignment__details--middle-school': isMiddleSchoolView,
          })}
        >
          <CardstackContent assignment={assignment} />
        </div>
      </div>
    </div>
  );
};
